import { Toaster } from '@trm/ui-kit';
import type { ReactNode } from 'react';
import { createContext, useLayoutEffect, useState } from 'react';

export type Theme = 'light' | 'dark' | 'system';

export interface ThemeContextType {
  theme: Theme;
  setTheme: (options: { theme: Theme | undefined; temporary?: boolean }) => void;
}

export const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  darkModeClass?: string;
  defaultTheme?: Theme;
  storageKey?: string;
}

export const UntitledUIThemeProvider = ({
  children,
  defaultTheme = 'system',
  storageKey = 'ui-theme',
  darkModeClass = 'dark-mode',
}: ThemeProviderProps) => {
  const [savedTheme, setSavedTheme] = useState<Theme>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(storageKey) as Theme | null;
      return stored || defaultTheme;
    }
    return defaultTheme;
  });
  const [overrideTheme, setOverrideTheme] = useState<Theme | undefined>();

  const activeTheme = overrideTheme ?? savedTheme;

  const handleSetTheme = (options: { theme: Theme | undefined; temporary?: boolean }) => {
    const { theme, temporary } = options;

    if (temporary) {
      setOverrideTheme(theme);
    } else {
      setSavedTheme(theme ?? 'system');
    }
  };

  useLayoutEffect(() => {
    const root = window.document.documentElement;

    const applyTheme = () => {
      if (activeTheme === 'system') {
        const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light';

        root.classList.toggle(darkModeClass, systemTheme === 'dark');
      } else {
        root.classList.toggle(darkModeClass, activeTheme === 'dark');
      }

      if (savedTheme === 'system') {
        localStorage.removeItem(storageKey);
      } else {
        localStorage.setItem(storageKey, savedTheme);
      }
    };

    applyTheme();

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = () => {
      if (activeTheme === 'system') {
        applyTheme();
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [activeTheme, savedTheme, darkModeClass, storageKey]);

  return (
    <ThemeContext.Provider value={{ theme: activeTheme, setTheme: handleSetTheme }}>
      <Toaster position="top-center" />
      {children}
    </ThemeContext.Provider>
  );
};
